import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UserStore } from '../../data-access/stores/user.store';
import { MatDialog } from '@angular/material/dialog';
import { QuestionnaireResponse } from '../../data-access/models/questionnaireResponse.model';
import { status, StatusEnum } from '../../enums/statusEnum';
import { DynamicDialogComponentComponent } from '../dynamic-dialog-component/dynamic-dialog-component.component';
import { Roles } from '../../../authentication/data-access/models/auth.model';
import { AverageModel } from '../../../university-dashboard/data-access/models/average.model';
import { STUDENT_DOCUMENT_CONFIGS, StudentDocumentConfig } from '../../../student/models/downloadedStudentDocumentData.model';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonAction } from "../../enums/buttonAction";
import { StudentDocumentLabel } from "../../../university-dashboard/enums/DocumentTypes";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackBarDuration } from "../../enums/snackBarDuration";
import { FileDownloadService } from "../../../university-dashboard/data-access/services/file-download-service.service";
import { isStudentDocumentBlob } from '../../utils/functions/documentBlob.function';

@Component({
    selector: 'app-university-information',
    templateUrl: './university-information.component.html',
    styleUrls: [
        './university-information.component.scss',
        '../../../university-dashboard/ui/student-details/student-details.component.scss',
        '../../utils/styling/sharedStudentDetails.scss'
    ],
    standalone: false
})
export class UniversityInformationComponent implements OnInit {

  @Input() student: any;
  @Input() applicationGuid!: string;
  @Input() enableEdit: boolean = false;
  @Input() editDetailsForm: FormGroup = new FormGroup({});
  @Output() openFileDialog = new EventEmitter<{ documentUrl:string, documentType:string }>();
  @Output() downloadDocument = new EventEmitter<{ documentUrl:string }>();
  @Output() academicRecordAverage = new EventEmitter<number>()
  @Output() updateGradeAverage = new EventEmitter<{ documentUrl: string, average: number }>();
  role: string = '';
  isOverviewer: boolean = false;
  isStudent: boolean = false;
  rolesEnum = Roles;
  showDocumentations: boolean;
  questionnaireResponses?: QuestionnaireResponse[];
  contract!: any;
  invoices!: any[];
  average!: AverageModel[];
  averages: any = {
    matric: 0,
    academicRecord: 0,
  };
  draft = status.DRAFT;
  pending = status.PENDING;
  awaitingStudentResponse = status.AWAITING_STUDENT_RESPONSE;
  emailFailed = status.EMAIL_FAILED;
  gridCols = { xs: 1, sm: 2, md: 4, lg: 6, xl: 8 };
  gradeAverageForms!: FormArray<FormGroup>;
  editingIndex: number | null = null;
  statusEnum = StatusEnum;
  
  constructor(
    public dialog: MatDialog,
    private userStore: UserStore,
    private fb: FormBuilder,
    private snackbar: MatSnackBar,
    private fileDownloadService: FileDownloadService
  ) {
    this.isOverviewer = this.userStore.hasRole([this.rolesEnum.OVERVIEWER]);
    this.isStudent = this.userStore.hasRole([this.rolesEnum.STUDENT]);
    this.showDocumentations = false;
  }
  ngOnInit(): void {

    if (this.student.StudentAverages) {
      this.average = this.student.StudentAverages
      this.average.forEach((average: any) => {
        average['semesterDescription'].toLocaleLowerCase() === 'matric'
          ? (this.averages['matric'] = average['semesterGradeAverage'])
          : average['semesterGradeAverage'] !== null &&
            average['semesterGradeAverage'] !== undefined
            ? (this.averages['academicRecord'] =
                average['semesterGradeAverage'])
            : null;
      });
    }
    this.academicRecordAverage.emit(this.averages['academicRecord'])
    if (this.student.QuestionnaireResponses) {
      this.questionnaireResponses = this.student.QuestionnaireResponses;
    }
    this.gradeAverageForms = this.fb.array(
      this.getAvailableDocuments().map(() =>
        this.fb.group({
          average: ['', [Validators.pattern(/^(100(\.0{1,2})?|[0-9]?\d(\.\d{1,2})?)$/)]]
        })
      )
    )
  }
  getYearOfStudyString(year: number): string {
    if (year) {
      if (year === 1) {
        return '1st year';
      } else if (year === 2) {
        return '2nd year';
      } else if (year === 3) {
        return '3rd year';
      }
      return year.toString();
    } else {
      return '-';
    }
  }

  viewDocument(documentName: string): void {
    const documentUrl: string = this.studentDocumentsMap[documentName];
    if (!this.isViewableDocument(documentUrl)) {
      this.displayMissingDocumentError(documentName);
    } else {
      this.fileDownloadService.viewBlobFile(documentUrl).subscribe(blobUrl => {
        window.open(blobUrl, '_blank');
      });
    }
  }

  onDownloadDocument(documentName: string): void {
    const documentUrl: string = this.studentDocumentsMap[documentName];
    if (!this.isViewableDocument(documentUrl)) {
      this.displayMissingDocumentError(documentName);
    } else {
      this.downloadDocument.emit({ documentUrl });
    }
  }

  onUpdateDocument(documentName: string): void {
    const documentUrl: string = this.studentDocumentsMap[documentName];
    if (!documentUrl) {
      this.displayMissingDocumentError(documentName);
      return;
    }
    this.openFileDialog.emit({ documentUrl: documentUrl, documentType:documentName });
  }

  isDocumentAvailable(propertyName: string): boolean {
    return !!(this.student && this.student[propertyName]);
  }

  isDocumentViewable(documentName: string): boolean {
    return this.isViewableDocument(this.studentDocumentsMap[documentName]);
  }

  viewQuestionnaire(): void {
    this.dialog.open(DynamicDialogComponentComponent, {
      data: {
        dialogHeader:`Questionnaire: ${this.student.name} ${this.student.surname}`,
        dialogContent: this.questionnaireResponses,
        studentName: this.student ? `${this.student.name}  ${this.student.surname}` : '',
      },
    });
  }

  getAverageGrade(documentName: string): boolean {
    return (
      this.averages &&
      this.averages[documentName] &&
      this.averages[documentName] >= 0
    );
  }

  getAvailableDocuments(): StudentDocumentConfig[] {
    return STUDENT_DOCUMENT_CONFIGS.filter(doc => this.isDocumentAvailable(doc.propertyName))
  }

  getAverageForm(index: number): FormGroup {
    return this.gradeAverageForms.at(index) as FormGroup;
  }

  startEditing(i: number) {
    this.editingIndex = i;
  }

  cancelEditing(i: number) {
    this.editingIndex = null;
    this.getAverageForm(i).get('average')?.setValue(null);
  }

  onUpdateGradeAverage(documentName: string, average: string): void {
    const numericAverage = parseFloat(average);
    const documentUrl = this.studentDocumentsMap[documentName];
    if (!documentUrl) {
      this.displayMissingDocumentError(documentName);
    } else {
      this.updateGradeAverage.emit({ documentUrl: documentUrl, average: numericAverage });
    }
  }

get shouldShowDetails(): boolean {
  return (
    this.student.status.toLowerCase() !== this.emailFailed &&
   this.student.status.toLowerCase() !== this.awaitingStudentResponse
  );
}

get shouldShowQuestionnaire(): boolean {
  return (
    ![this.pending, this.draft, this.awaitingStudentResponse, this.emailFailed].includes(
      this.student.status.toLowerCase()
    ) && (this.isOverviewer || this.isStudent)
  );
}

  get studentDocumentsMap(): { [key: string]: string } {
    return {
      [StudentDocumentLabel.MATRIC_CERTIFICATE] : this.student.matricCertificate,
      [StudentDocumentLabel.ACADEMIC_RECORD]: this.student.academicTranscript,
      [StudentDocumentLabel.PROOF_OF_IDENTIFICATION]: this.student.identityDocument,
      [StudentDocumentLabel.FINANCIAL_STATEMENT]: this.student.financialStatement,
    };
  }

  displayMissingDocumentError(documentName: string): void{
    this.snackbar.open(`${documentName} is not available`, '', {
      duration: SnackBarDuration.DURATION
    })
  }

  private isViewableDocument(blobName: string | undefined): boolean {
    return isStudentDocumentBlob(blobName);
  }

  protected readonly ButtonAction = ButtonAction;
}
